import { Container, Graphics } from 'pixi.js';
import { worldToScreen, depthSortKey } from '../iso/IsoGrid';
import { drawFloorDiamond } from '../art/IsoShapes';
import { getFurnitureDrawFn } from '../art/Furniture';
import { FLOOR_PALETTES, type FloorPalette } from '../art/colors';

export interface TileDef {
  x: number;
  y: number;
  type: string;
}

export class OfficeRenderer {
  private container: Container;
  private floorGraphics: Graphics;
  private objectGraphics: Graphics;

  constructor() {
    this.container = new Container();
    this.container.sortableChildren = true;

    this.floorGraphics = new Graphics();
    this.floorGraphics.zIndex = 0;

    this.objectGraphics = new Graphics();
    this.objectGraphics.zIndex = 1;

    this.container.addChild(this.floorGraphics);
    this.container.addChild(this.objectGraphics);
  }

  get displayObject(): Container {
    return this.container;
  }

  render(tiles: TileDef[], gridWidth: number, gridHeight: number, officeLevel: number = 1): void {
    this.floorGraphics.clear();
    this.objectGraphics.clear();

    const palette = FLOOR_PALETTES[Math.min(officeLevel - 1, FLOOR_PALETTES.length - 1)] ?? FLOOR_PALETTES[0]!;

    this.drawFloor(gridWidth, gridHeight, palette);
    this.drawWalls(gridWidth, gridHeight, palette);
    this.drawObjects(tiles);
  }

  private drawFloor(gridWidth: number, gridHeight: number, palette: FloorPalette): void {
    for (let x = 0; x < gridWidth; x++) {
      for (let y = 0; y < gridHeight; y++) {
        const screen = worldToScreen(x, y);
        const isAlt = (x + y) % 2 === 0;
        drawFloorDiamond(
          this.floorGraphics,
          screen.x,
          screen.y,
          isAlt ? palette.tile : palette.tileAlt,
        );
      }
    }
  }

  private drawWalls(gridWidth: number, gridHeight: number, palette: FloorPalette): void {
    const g = this.floorGraphics;
    const wallH = 14;
    const wallColor = palette.gridLine;
    const TW = 64;
    const TH = 32;

    // The floor grid is drawn for tiles (0,0) to (W-1, H-1).
    // Each tile diamond center is at worldToScreen(x, y).
    // The OUTER BOUNDARY of the grid is at the edges of the outermost tiles:
    //   Top point:    top of tile(0,0) = worldToScreen(0,0) + (0, -TH/2)
    //   Left point:   left of tile(0, H-1) = worldToScreen(0, H-1) + (-TW/2, 0)
    //   Right point:  right of tile(W-1, 0) = worldToScreen(W-1, 0) + (TW/2, 0)
    //   Bottom point: bottom of tile(W-1, H-1) = worldToScreen(W-1, H-1) + (0, TH/2)

    const c00 = worldToScreen(0, 0);
    const cLeft = worldToScreen(0, gridHeight - 1);
    const cRight = worldToScreen(gridWidth - 1, 0);

    const topPt =   { x: c00.x,              y: c00.y - TH / 2 };
    const leftPt =  { x: cLeft.x - TW / 2,   y: cLeft.y };
    const rightPt = { x: cRight.x + TW / 2,   y: cRight.y };

    // Left wall: from topPt going down-left to leftPt, extruded upward
    g.poly([
      topPt.x,  topPt.y,          // floor level at top
      leftPt.x, leftPt.y,         // floor level at left
      leftPt.x, leftPt.y - wallH, // wall top at left
      topPt.x,  topPt.y - wallH,  // wall top at top
    ]);
    g.fill({ color: wallColor, alpha: 0.35 });

    // Right wall: from topPt going down-right to rightPt, extruded upward
    g.poly([
      topPt.x,   topPt.y,           // floor level at top
      rightPt.x, rightPt.y,         // floor level at right
      rightPt.x, rightPt.y - wallH, // wall top at right
      topPt.x,   topPt.y - wallH,   // wall top at top
    ]);
    g.fill({ color: wallColor, alpha: 0.25 });

    // Wall top edge lines
    g.moveTo(topPt.x, topPt.y - wallH);
    g.lineTo(leftPt.x, leftPt.y - wallH);
    g.stroke({ color: wallColor, width: 1.5, alpha: 0.6 });

    g.moveTo(topPt.x, topPt.y - wallH);
    g.lineTo(rightPt.x, rightPt.y - wallH);
    g.stroke({ color: wallColor, width: 1.5, alpha: 0.6 });

    // Corner post
    g.moveTo(topPt.x, topPt.y);
    g.lineTo(topPt.x, topPt.y - wallH);
    g.stroke({ color: wallColor, width: 2, alpha: 0.7 });
  }

  private drawObjects(tiles: TileDef[]): void {
    const sortedTiles = [...tiles]
      .sort((a, b) => depthSortKey(a.x, a.y) - depthSortKey(b.x, b.y));

    for (const tile of sortedTiles) {
      const screen = worldToScreen(tile.x, tile.y);
      const drawFn = getFurnitureDrawFn(tile.type);
      if (drawFn) {
        drawFn(this.objectGraphics, screen.x, screen.y);
      }
    }
  }

  clear(): void {
    this.floorGraphics.clear();
    this.objectGraphics.clear();
  }
}
